import TenantBrandingFields from './TenantBrandingFields';
import { OpenRouterModelSelect } from "./ManagedAI";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { pianiAPI, aiAPI, apiClient } from '../utils/api';

interface Piano {
  id: string;
  nome: string;
  slug: string;
  max_utenti: number;
  max_offerte_mese: number;
  max_storage_mb: number;
  feat_ai_custom_key?: boolean;
}

interface Tenant {
  id: string;
  nome: string;
  slug: string;
  email_admin: string;
  piano_id: string;
  piano?: Piano;
  partita_iva?: string;
  indirizzo?: string;
  telefono?: string;
  email?: string;
  sito_web?: string;
  logo_url?: string;
  mostra_nome_tenant?: boolean;
  colore_primario?: string;
  colore_secondario?: string;
  wms_enabled?: boolean;
  wms_tenant_id?: string | null;
  is_active: boolean;
}

interface TenantFormData {
  nome: string;
  slug: string;
  email_admin: string;
  piano_id: string;
  partita_iva?: string;
  indirizzo?: string;
  telefono?: string;
  email?: string;
  sito_web?: string;
  logo_url?: string;
  mostra_nome_tenant?: boolean;
  colore_primario?: string;
  colore_secondario?: string;
  wms_enabled?: boolean;
  wms_tenant_id?: string | null;
  is_active: boolean;
}

interface TenantFormProps {
  tenant?: Tenant | null;
  onSubmit: (data: TenantFormData) => void;
  isLoading?: boolean;
}

interface AIConfigFormData {
  managed_by_host: boolean;
  spend_limit_usd: number;
  allow_fallbacks: boolean;
  data_collection: string;
  provider_sort: string;
  top_p: number;

  provider: string;
  model: string;
  api_key: string;
  api_endpoint: string;
  ai_enabled: boolean;
  ai_suggestions_enabled: boolean;
  ai_generation_enabled: boolean;
  ai_improvement_enabled: boolean;
  max_tokens_per_request: number;
  max_requests_per_hour: number;
  max_tokens_per_month: number;
  temperature: number;
  max_output_tokens: number;
}

const PROVIDER_MODELS: Record<string, string[]> = {
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  anthropic: ['claude-sonnet-4-5-20250514', 'claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022', 'claude-3-opus-20240229'],
  ollama: ['llama3', 'llama3.1', 'mistral', 'codellama', 'gemma2'],
};

const AI_CONFIG_DEFAULTS: AIConfigFormData = {
  managed_by_host: false, spend_limit_usd: 0, allow_fallbacks: true, data_collection: 'deny', provider_sort: 'price', top_p: 1,
  provider: 'openai',
  model: 'gpt-4o',
  api_key: '',
  api_endpoint: '',
  ai_enabled: true,
  ai_suggestions_enabled: true,
  ai_generation_enabled: true,
  ai_improvement_enabled: true,
  max_tokens_per_request: 4000,
  max_requests_per_hour: 100,
  max_tokens_per_month: 1000000,
  temperature: 0.7,
  max_output_tokens: 2000,
};

type TabKey = 'generali' | 'azienda' | 'branding' | 'ai';

const AIUsagePanel: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ['ai-usage-tenant', tenantId],
    queryFn: async () => {
      const response = await aiAPI.getUsageStatsForTenant(tenantId);
      return response.data;
    },
    staleTime: 30000,
    retry: false,
  });

  if (usageLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" />
          <span className="ml-2 text-sm text-gray-500">Caricamento utilizzo AI...</span>
        </div>
      </div>
    );
  }

  if (!usageData || !usageData.limits) return null;

  const requestsUsed = usageData.requests_this_hour ?? 0;
  const requestsLimit = usageData.limits.max_requests_per_hour ?? 0;
  const tokensUsed = usageData.tokens_this_month ?? 0;
  const tokensLimit = usageData.limits.max_tokens_per_month ?? 0;

  const requestsPct = requestsLimit > 0 ? Math.min((requestsUsed / requestsLimit) * 100, 100) : 0;
  const tokensPct = tokensLimit > 0 ? Math.min((tokensUsed / tokensLimit) * 100, 100) : 0;

  const getBarColor = (pct: number) => {
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const formatNumber = (n: number) => n.toLocaleString('it-IT');

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Utilizzo AI</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Requests this hour */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600">Richieste questa ora</span>
            <span className="text-sm font-medium text-gray-900">{Math.round(requestsPct)}%</span>
          </div>
          <div className="text-lg font-semibold text-gray-900 mb-2">
            {formatNumber(requestsUsed)} / {formatNumber(requestsLimit)}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getBarColor(requestsPct)}`}
              style={{ width: `${requestsPct}%` }}
            />
          </div>
        </div>

        {/* Tokens this month */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600">Token questo mese</span>
            <span className="text-sm font-medium text-gray-900">{Math.round(tokensPct)}%</span>
          </div>
          <div className="text-lg font-semibold text-gray-900 mb-2">
            {formatNumber(tokensUsed)} / {formatNumber(tokensLimit)}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${getBarColor(tokensPct)}`}
              style={{ width: `${tokensPct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const TenantForm: React.FC<TenantFormProps> = ({ tenant, onSubmit, isLoading }) => {
  const [logoUploading, setLogoUploading] = useState(false);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('generali');
  const [aiForm, setAiForm] = useState<AIConfigFormData>(AI_CONFIG_DEFAULTS);
  const [showApiKey, setShowApiKey] = useState(false);
  const [aiSaveMessage, setAiSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const tabs: { key: TabKey; label: string; editOnly?: boolean }[] = [
    { key: 'generali', label: 'Generali' },
    { key: 'azienda', label: 'Azienda' },
    { key: 'branding', label: 'Branding' },
    { key: 'ai', label: 'AI', editOnly: true },
  ];

  // Fetch piani from API
  const { data: pianiData, isLoading: pianiLoading } = useQuery({
    queryKey: ['piani'],
    queryFn: async () => {
      const response = await pianiAPI.getPiani({ is_active: true });
      return response.data;
    },
  });

  // Fetch AI config for this tenant (only in edit mode)
  const { data: aiConfigData, isLoading: aiConfigLoading } = useQuery({
    queryKey: ['ai-config-tenant', tenant?.id],
    queryFn: async () => {
      const response = await aiAPI.getConfigForTenant(tenant!.id);
      return response.data;
    },
    enabled: !!tenant?.id,
    retry: false,
  });

  const aiConfigExists = !!aiConfigData && !('detail' in aiConfigData);

  // Populate AI form when config is loaded
  useEffect(() => {
    if (aiConfigData && aiConfigExists) {
      setAiForm({
        managed_by_host: aiConfigData.managed_by_host ?? false,
        spend_limit_usd: aiConfigData.spend_limit_usd ?? 0,
        allow_fallbacks: aiConfigData.provider_settings?.allow_fallbacks ?? true,
        data_collection: aiConfigData.provider_settings?.data_collection ?? 'deny',
        provider_sort: aiConfigData.provider_settings?.sort ?? 'price',
        top_p: aiConfigData.provider_settings?.top_p ?? 1,
        provider: aiConfigData.provider || 'openai',
        model: aiConfigData.model || 'gpt-4o',
        api_key: '',
        api_endpoint: aiConfigData.api_endpoint || '',
        ai_enabled: aiConfigData.ai_enabled ?? true,
        ai_suggestions_enabled: aiConfigData.ai_suggestions_enabled ?? true,
        ai_generation_enabled: aiConfigData.ai_generation_enabled ?? true,
        ai_improvement_enabled: aiConfigData.ai_improvement_enabled ?? true,
        max_tokens_per_request: aiConfigData.max_tokens_per_request ?? 4000,
        max_requests_per_hour: aiConfigData.max_requests_per_hour ?? 100,
        max_tokens_per_month: aiConfigData.max_tokens_per_month ?? 1000000,
        temperature: aiConfigData.temperature ?? 0.7,
        max_output_tokens: aiConfigData.max_output_tokens ?? 2000,
      });
    }
  }, [aiConfigData, aiConfigExists]);

  const provisionAI = useMutation({
    mutationFn: async () => {
      const result = await apiClient.client.post(`/platform-settings/ai/${tenant!.id}/provision`);
      if (!result.data.success) throw new Error(result.data.message);
      return result.data;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ai-config-tenant', tenant?.id] }); setAiSaveMessage({ type: 'success', text: 'Workspace AI pronto' }); },
    onError: (e: any) => { queryClient.invalidateQueries({ queryKey: ['ai-config-tenant', tenant?.id] }); setAiSaveMessage({ type: 'error', text: e.response?.data?.detail || e.message }); },
  });

  // AI config mutations
  const createAIConfigMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => aiAPI.createConfigForTenant(tenant!.id, data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['ai-config-tenant', tenant?.id] });
      if (response.data?.provisioning_error) { setAiSaveMessage({ type: 'error', text: response.data.provisioning_error }); return; }
      setAiSaveMessage({ type: 'success', text: 'Configurazione AI creata con successo' });
      setTimeout(() => setAiSaveMessage(null), 3000);
    },
    onError: (error: any) => {
      setAiSaveMessage({ type: 'error', text: error.response?.data?.detail || 'Errore nel salvataggio' });
      setTimeout(() => setAiSaveMessage(null), 5000);
    },
  });

  const updateAIConfigMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => aiAPI.updateConfigForTenant(tenant!.id, data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['ai-config-tenant', tenant?.id] });
      if (response.data?.provisioning_error) { setAiSaveMessage({ type: 'error', text: response.data.provisioning_error }); return; }
      setAiSaveMessage({ type: 'success', text: 'Configurazione AI aggiornata con successo' });
      setTimeout(() => setAiSaveMessage(null), 3000);
    },
    onError: (error: any) => {
      setAiSaveMessage({ type: 'error', text: error.response?.data?.detail || 'Errore nel salvataggio' });
      setTimeout(() => setAiSaveMessage(null), 5000);
    },
  });

  const handleSaveAIConfig = () => {
    const payload: Record<string, unknown> = {
      managed_by_host: aiForm.provider === 'openrouter' && aiForm.managed_by_host,
      spend_limit_usd: aiForm.provider === 'openrouter' && aiForm.managed_by_host ? aiForm.spend_limit_usd : null,
      provider_settings: aiForm.provider === 'openrouter' ? { allow_fallbacks: aiForm.allow_fallbacks, data_collection: aiForm.data_collection, sort: aiForm.provider_sort } : { top_p: aiForm.top_p },
      provider: aiForm.provider,
      model: aiForm.model,
      ai_enabled: aiForm.ai_enabled,
      ai_suggestions_enabled: aiForm.ai_suggestions_enabled,
      ai_generation_enabled: aiForm.ai_generation_enabled,
      ai_improvement_enabled: aiForm.ai_improvement_enabled,
      max_tokens_per_request: aiForm.max_tokens_per_request,
      max_requests_per_hour: aiForm.max_requests_per_hour,
      max_tokens_per_month: aiForm.max_tokens_per_month,
      temperature: aiForm.temperature,
      max_output_tokens: aiForm.max_output_tokens,
    };

    if (aiForm.api_key && !aiForm.managed_by_host) {
      payload.api_key = aiForm.api_key;
    }
    if (aiForm.api_endpoint) {
      payload.api_endpoint = aiForm.api_endpoint;
    }

    if (aiConfigExists) {
      updateAIConfigMutation.mutate(payload);
    } else {
      createAIConfigMutation.mutate(payload);
    }
  };

  const handleAIFieldChange = (field: keyof AIConfigFormData, value: string | number | boolean) => {
    setAiForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'provider') {
        updated.managed_by_host = false;
        if (value === 'openrouter') {
          updated.model = 'openai/gpt-4o-mini';
        } else {
          const models = PROVIDER_MODELS[value as string] || [];
          updated.model = models[0] || '';
        }
      }
      return updated;
    });
  };

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<TenantFormData>({
    defaultValues: {
      nome: '',
      slug: '',
      email_admin: '',
      piano_id: '',
      is_active: true,
      wms_enabled: false,
      wms_tenant_id: null,
      mostra_nome_tenant: true,
      colore_primario: '#2563eb',
      colore_secondario: '#1e40af',
    }
  });

  const selectedPianoId = watch('piano_id');
  const selectedPiano = pianiData?.data.find((p: Piano) => p.id === selectedPianoId);

  // Reset form when tenant changes and piani are loaded
  useEffect(() => {
    if (tenant && pianiData) {
      reset({
        nome: tenant.nome,
        slug: tenant.slug,
        email_admin: tenant.email_admin,
        piano_id: tenant.piano_id,
        partita_iva: tenant.partita_iva,
        indirizzo: tenant.indirizzo,
        telefono: tenant.telefono,
        email: tenant.email,
        sito_web: tenant.sito_web,
        logo_url: tenant.logo_url,
        mostra_nome_tenant: tenant.mostra_nome_tenant ?? true,
        colore_primario: tenant.colore_primario || '#2563eb',
        colore_secondario: tenant.colore_secondario || '#1e40af',
        is_active: tenant.is_active,
        wms_enabled: tenant.wms_enabled ?? false,
        wms_tenant_id: tenant.wms_tenant_id ?? null,
      });
    }
  }, [tenant, pianiData, reset]);

  const availableModels = PROVIDER_MODELS[aiForm.provider] || [];
  const aiSaving = createAIConfigMutation.isPending || updateAIConfigMutation.isPending;
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  const visibleTabs = tabs.filter(t => !t.editOnly || !!tenant);

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {visibleTabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.key === 'ai' && aiConfigExists && (
                <span className="ml-2 inline-block w-2 h-2 bg-green-400 rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab: Generali */}
      {activeTab === 'generali' && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informazioni Base</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nome Tenant *</label>
                <input
                  type="text"
                  {...register('nome', { required: 'Nome richiesto' })}
                  className={inputClass}
                />
                {errors.nome && <p className="text-red-600 text-sm mt-1">{errors.nome.message}</p>}
              </div>

              <div>
                <label className={labelClass}>Slug *</label>
                <input
                  type="text"
                  {...register('slug', {
                    required: 'Slug richiesto',
                    pattern: {
                      value: /^[a-z0-9-]+$/,
                      message: 'Solo lettere minuscole, numeri e trattini'
                    }
                  })}
                  className={inputClass}
                  placeholder="esempio-tenant"
                />
                {errors.slug && <p className="text-red-600 text-sm mt-1">{errors.slug.message}</p>}
              </div>

              <div>
                <label className={labelClass}>Email Admin *</label>
                <input
                  type="email"
                  {...register('email_admin', {
                    required: 'Email richiesta',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email non valida'
                    }
                  })}
                  className={inputClass}
                />
                {errors.email_admin && <p className="text-red-600 text-sm mt-1">{errors.email_admin.message}</p>}
              </div>

              <div>
                <label className={labelClass}>Piano *</label>
                <select
                  {...register('piano_id', { required: 'Piano richiesto' })}
                  className={`${inputClass} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  disabled={pianiLoading}
                >
                  {pianiLoading ? (
                    <option value="">Caricamento piani...</option>
                  ) : (
                    <>
                      <option value="">Seleziona un piano...</option>
                      {pianiData?.data.map((piano: Piano) => (
                        <option key={piano.id} value={piano.id}>{piano.nome}</option>
                      ))}
                    </>
                  )}
                </select>
                {errors.piano_id && <p className="text-red-600 text-sm mt-1">{errors.piano_id.message}</p>}
              </div>
            </div>
          </div>

          {selectedPiano && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Limiti del Piano Selezionato</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-blue-700 font-medium">Max Utenti:</span>
                  <span className="ml-2 text-blue-900">{selectedPiano.max_utenti}</span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Max Offerte/Mese:</span>
                  <span className="ml-2 text-blue-900">{selectedPiano.max_offerte_mese}</span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Storage:</span>
                  <span className="ml-2 text-blue-900">{selectedPiano.max_storage_mb} MB</span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-6">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('is_active')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Tenant Attivo</span>
            </label>
          </div>

          <div className="bg-white rounded-lg shadow p-6 space-y-3">
            <label className="flex items-center gap-2"><input type="checkbox" {...register('wms_enabled')} />Abilita funzione WMS</label>
            {watch('wms_enabled') && <div>
              <label htmlFor="tenant-wms-id" className="block text-sm font-medium">Tenant associato in WMS</label>
              <input id="tenant-wms-id" {...register('wms_tenant_id', { required: 'Indica il tenant WMS', pattern: { value: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, message: 'Inserisci un UUID valido' }, setValueAs: (value) => value || null })} className="mt-1 w-full rounded-lg border px-3 py-2" placeholder="UUID del tenant in Warehouse" />
              {errors.wms_tenant_id && <p className="text-sm text-red-600">{errors.wms_tenant_id.message}</p>}
              <p className="text-xs text-gray-500 mt-1">L'associazione viene usata dalla configurazione OAuth del tenant.</p>
            </div>}
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading || logoUploading}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              {tenant ? 'Salva Modifiche' : 'Crea Tenant'}
            </button>
          </div>
        </form>
      )}

      {/* Tab: Azienda */}
      {activeTab === 'azienda' && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dati Azienda</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Partita IVA</label>
                <input type="text" {...register('partita_iva')} className={inputClass} />
              </div>

              <div>
                <label className={labelClass}>Telefono</label>
                <input type="tel" {...register('telefono')} className={inputClass} />
              </div>

              <div>
                <label className={labelClass}>Email Aziendale</label>
                <input type="email" {...register('email')} className={inputClass} />
              </div>

              <div>
                <label className={labelClass}>Sito Web</label>
                <input
                  type="url"
                  {...register('sito_web')}
                  className={inputClass}
                  placeholder="https://esempio.it"
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Indirizzo</label>
                <textarea {...register('indirizzo')} rows={2} className={inputClass} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 space-y-3">
            <label className="flex items-center gap-2"><input type="checkbox" {...register('wms_enabled')} />Abilita funzione WMS</label>
            {watch('wms_enabled') && <div>
              <label htmlFor="tenant-wms-id" className="block text-sm font-medium">Tenant associato in WMS</label>
              <input id="tenant-wms-id" {...register('wms_tenant_id', { required: 'Indica il tenant WMS', pattern: { value: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, message: 'Inserisci un UUID valido' }, setValueAs: (value) => value || null })} className="mt-1 w-full rounded-lg border px-3 py-2" placeholder="UUID del tenant in Warehouse" />
              {errors.wms_tenant_id && <p className="text-sm text-red-600">{errors.wms_tenant_id.message}</p>}
              <p className="text-xs text-gray-500 mt-1">L'associazione viene usata dalla configurazione OAuth del tenant.</p>
            </div>}
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading || logoUploading}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              {tenant ? 'Salva Modifiche' : 'Crea Tenant'}
            </button>
          </div>
        </form>
      )}

      {/* Tab: Branding */}
      {activeTab === 'branding' && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Branding</h3>
            <TenantBrandingFields tenantId={tenant?.id}
              value={{ logo_url: watch('logo_url'), mostra_nome_tenant: watch('mostra_nome_tenant'), colore_primario: watch('colore_primario'), colore_secondario: watch('colore_secondario') }}
              onChange={(changes) => {
                if ('logo_url' in changes) setValue('logo_url', changes.logo_url || '', { shouldDirty: true });
                if ('mostra_nome_tenant' in changes) setValue('mostra_nome_tenant', changes.mostra_nome_tenant, { shouldDirty: true });
                if ('colore_primario' in changes) setValue('colore_primario', changes.colore_primario, { shouldDirty: true });
                if ('colore_secondario' in changes) setValue('colore_secondario', changes.colore_secondario, { shouldDirty: true });
              }} onBusyChange={setLogoUploading} />
          </div>

          <div className="bg-white rounded-lg shadow p-6 space-y-3">
            <label className="flex items-center gap-2"><input type="checkbox" {...register('wms_enabled')} />Abilita funzione WMS</label>
            {watch('wms_enabled') && <div>
              <label htmlFor="tenant-wms-id" className="block text-sm font-medium">Tenant associato in WMS</label>
              <input id="tenant-wms-id" {...register('wms_tenant_id', { required: 'Indica il tenant WMS', pattern: { value: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, message: 'Inserisci un UUID valido' }, setValueAs: (value) => value || null })} className="mt-1 w-full rounded-lg border px-3 py-2" placeholder="UUID del tenant in Warehouse" />
              {errors.wms_tenant_id && <p className="text-sm text-red-600">{errors.wms_tenant_id.message}</p>}
              <p className="text-xs text-gray-500 mt-1">L'associazione viene usata dalla configurazione OAuth del tenant.</p>
            </div>}
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading || logoUploading}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              {tenant ? 'Salva Modifiche' : 'Crea Tenant'}
            </button>
          </div>
        </form>
      )}

      {/* Tab: AI (only in edit mode) */}
      {activeTab === 'ai' && tenant && (
        <div className="space-y-6">
          {/* AI Usage Counters */}
          <AIUsagePanel tenantId={tenant.id} />

          <div className="bg-white rounded-lg shadow p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Configurazione AI</h3>
              {aiConfigExists && (
                <span className="text-xs font-medium bg-green-100 text-green-800 px-2.5 py-1 rounded-full">
                  Configurato
                </span>
              )}
              {!aiConfigExists && !aiConfigLoading && (
                <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
                  Non configurato
                </span>
              )}
            </div>

            {selectedPiano?.feat_ai_custom_key && (
              <div className="text-sm bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-lg px-3 py-2">
                Il tenant può gestire la propria chiave API (feature del piano "{selectedPiano.nome}")
              </div>
            )}

            {aiConfigLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
                <span className="ml-2 text-gray-500">Caricamento configurazione AI...</span>
              </div>
            ) : (
              <>
                {/* Provider & Model */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Provider</label>
                    <select
                      value={aiForm.provider}
                      onChange={(e) => handleAIFieldChange('provider', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="openai">OpenAI</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="ollama">Ollama</option>
                      <option value="openrouter">OpenRouter</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Modello</label>
                    {aiForm.provider === 'openrouter' ? (
                      <OpenRouterModelSelect value={aiForm.model} onChange={m => handleAIFieldChange('model', m)} />
                    ) : (
                      <select
                        value={aiForm.model}
                        onChange={(e) => handleAIFieldChange('model', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        {availableModels.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {aiForm.provider === 'openrouter' && <div className="space-y-3">
                  <label className="flex gap-2"><input type="checkbox" checked={aiForm.managed_by_host} onChange={e => handleAIFieldChange('managed_by_host', e.target.checked)} />Usa provisioning e credenziali OpenRouter della piattaforma</label>
                  {aiForm.managed_by_host && <>
                    <label className={labelClass}>Budget mensile (USD)<input type="number" min="0" max="100000" step="0.01" value={aiForm.spend_limit_usd} onChange={e => handleAIFieldChange('spend_limit_usd', Number(e.target.value))} className="block w-full border rounded p-2" /></label>
                    <p className="text-xs text-gray-500">Budget applicato alla chiave OpenRouter. Zero blocca la spesa; rinnovo mensile UTC.</p>
                    <p className="text-sm">Workspace: {aiConfigData?.workspace_id || 'Da creare al salvataggio'}</p>
                    {aiConfigData?.provisioning_error && <p role="alert" className="text-red-600">{aiConfigData.provisioning_error}</p>}
                    {aiConfigExists && <button type="button" className="text-primary-600" disabled={provisionAI.isPending} onClick={() => provisionAI.mutate()}>Verifica / ripara workspace AI</button>}
                  </>}
                  <label className="flex gap-2"><input type="checkbox" checked={aiForm.allow_fallbacks} onChange={e => handleAIFieldChange('allow_fallbacks', e.target.checked)} />Consenti fallback tra fornitori OpenRouter</label>
                  <label className={labelClass}>Raccolta dati<select className="block border rounded p-2" value={aiForm.data_collection} onChange={e => handleAIFieldChange('data_collection', e.target.value)}><option value="deny">Solo fornitori che non raccolgono dati</option><option value="allow">Consenti raccolta dati</option></select></label>
                  <label className={labelClass}>Priorità instradamento<select className="block border rounded p-2" value={aiForm.provider_sort} onChange={e => handleAIFieldChange('provider_sort', e.target.value)}><option value="price">Prezzo</option><option value="latency">Latenza</option><option value="throughput">Velocità</option></select></label>
                </div>}
                {aiForm.provider !== 'openrouter' && <label className={labelClass}>Top P<input type="number" min="0" max="1" step="0.05" value={aiForm.top_p} onChange={e => handleAIFieldChange('top_p', Number(e.target.value))} className="block border rounded p-2" /></label>}
                {!aiForm.managed_by_host && <p className="text-xs text-gray-500">Per credenziali dedicate, il limite di spesa si configura presso il provider.</p>}
                {/* API Key & Endpoint */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    {!aiForm.managed_by_host && <>
                    <label className={labelClass}>
                      API Key
                      {aiConfigExists && aiConfigData?.has_api_key && (
                        <span className="ml-2 text-xs text-green-600">(impostata)</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        value={aiForm.api_key}
                        onChange={(e) => handleAIFieldChange('api_key', e.target.value)}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder={aiConfigExists && aiConfigData?.has_api_key ? 'Lascia vuoto per mantenere' : 'sk-...'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showApiKey ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    </div>
                    </>}
                  </div>

                  {aiForm.provider === 'ollama' && (
                    <div>
                      <label className={labelClass}>API Endpoint</label>
                      <input
                        type="text"
                        value={aiForm.api_endpoint}
                        onChange={(e) => handleAIFieldChange('api_endpoint', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="http://localhost:11434"
                      />
                    </div>
                  )}
                </div>

                {/* Feature Flags */}
                <div>
                  <label className={`${labelClass} mb-2`}>Funzionalita</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={aiForm.ai_enabled}
                        onChange={(e) => handleAIFieldChange('ai_enabled', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">AI Attivo</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={aiForm.ai_suggestions_enabled}
                        onChange={(e) => handleAIFieldChange('ai_suggestions_enabled', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Suggerimenti</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={aiForm.ai_generation_enabled}
                        onChange={(e) => handleAIFieldChange('ai_generation_enabled', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Generazione</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={aiForm.ai_improvement_enabled}
                        onChange={(e) => handleAIFieldChange('ai_improvement_enabled', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Miglioramento</span>
                    </label>
                  </div>
                </div>

                {/* Rate Limits */}
                <div>
                  <label className={`${labelClass} mb-2`}>Limiti</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Max Token/Richiesta</label>
                      <input
                        type="number"
                        value={aiForm.max_tokens_per_request}
                        onChange={(e) => handleAIFieldChange('max_tokens_per_request', parseInt(e.target.value) || 0)}
                        min={100}
                        max={16000}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Max Richieste/Ora</label>
                      <input
                        type="number"
                        value={aiForm.max_requests_per_hour}
                        onChange={(e) => handleAIFieldChange('max_requests_per_hour', parseInt(e.target.value) || 0)}
                        min={1}
                        max={10000}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Max Token/Mese</label>
                      <input
                        type="number"
                        value={aiForm.max_tokens_per_month}
                        onChange={(e) => handleAIFieldChange('max_tokens_per_month', parseInt(e.target.value) || 0)}
                        min={1000}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Temperature & Max Output Tokens */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>
                      Temperature: {aiForm.temperature.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      value={aiForm.temperature}
                      onChange={(e) => handleAIFieldChange('temperature', parseFloat(e.target.value))}
                      min={0}
                      max={2}
                      step={0.1}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>0 (Preciso)</span>
                      <span>2 (Creativo)</span>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Max Output Tokens</label>
                    <input
                      type="number"
                      value={aiForm.max_output_tokens}
                      onChange={(e) => handleAIFieldChange('max_output_tokens', parseInt(e.target.value) || 0)}
                      min={100}
                      max={8000}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Save AI Config Button */}
          {!aiConfigLoading && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSaveAIConfig}
                disabled={aiSaving}
                className="inline-flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {aiSaving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                {aiConfigExists ? 'Aggiorna Configurazione AI' : 'Crea Configurazione AI'}
              </button>
              {aiSaveMessage && (
                <span className={`text-sm ${aiSaveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {aiSaveMessage.text}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantForm;
